// P5 sheets (frozen mockups-rnd-v1): SCR-RND-02 truck, -03 change Seller, -05 open round, -06 stock movement,
// -07 truck count, -08 end selling and reopen. The database decides (live rounds, balances, roles).
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Chips,
  Field,
  Input,
  List,
  ListRow,
  Notice,
  Select,
  SheetActions,
  Tabs,
} from '../../design/kit';
import { useSession } from '../../app/session';
import { Refusal } from '../../data/auth';
import { loadUsers } from '../../data/settings';
import {
  ROUNDS,
  changeTruckSeller,
  countTruck,
  endSelling,
  moveStock,
  openRound,
  reopenRound,
  saveTruck,
  type Round,
  type Truck,
} from '../../data/rounds';
import type { InventoryRow } from '../../data/stock';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { LineSheet, ReasonField, countLines, countWord, useDone, useStockWords, type Line } from '../stock/StockSheets';

const VIN = /^[A-HJ-NPR-Z0-9]{17}$/;
type Problem = { field: string; text: string };

function useRoundWords() {
  const { t } = useTranslation();
  const refusal = (err: unknown): Problem => {
    if (!(err instanceof Refusal)) return { field: 'form', text: t(messageKey('SERVER_ERROR')) };
    const d = err.detail;
    switch (err.code) {
      case 'VIN_TAKEN':
        return { field: 'vin', text: t('err.vintaken', { t: String(d.truck ?? '') }) };
      case 'INVALID_VIN':
        return { field: 'vin', text: t('err.vin') };
      case 'LIVE_ROUND':
        return { field: 'form', text: t('err.liveround', { s: t(d.status === 'open' ? 'st.open' : 'st.ended') }) };
      case 'ROUND_LIVE':
        return { field: 'form', text: t('err.roundlive', { r: String(d.round ?? '') }) };
      case 'REASON_REQUIRED':
        return { field: 'reason', text: t('err.reason') };
      default:
        return { field: 'form', text: t(messageKey(err.code)) };
    }
  };
  return { refusal };
}

function useRoundsDone(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ROUNDS });
    void qc.invalidateQueries({ queryKey: ['stock'] });
    onClose();
  };
}

function useSellers() {
  return useQuery({ queryKey: ['settings', 'v_users'], queryFn: loadUsers });
}

/** Who a truck can go to: the active Sellers, and the owner themselves, first (ADR-135). */
function useSellerChoices(excludeId?: string | undefined) {
  const { t } = useTranslation();
  const { role, userId } = useSession();
  const users = useSellers();
  const people = (users.data ?? []).filter((u) => u.active && u.user_id !== excludeId);
  const me = role === 'owner' ? people.filter((u) => u.user_id === userId) : [];
  const sellers = people.filter((u) => u.role === 'seller');
  return {
    choices: [
      ...me.map((u) => ({ id: u.user_id, label: `${t('own.me')} · ${u.display_name}` })),
      ...sellers.map((u) => ({ id: u.user_id, label: u.display_name })),
    ],
  };
}

/** SCR-RND-02: register a truck (with its Seller) or edit one; owner only (TRK-01, ADR-100). */
export function TruckSheet({ truck, onClose }: { truck?: Truck | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = useRoundWords();
  const done = useRoundsDone(onClose);
  const { choices } = useSellerChoices();
  const [category, setCategory] = useState(truck?.category ?? '');
  const [vin, setVin] = useState(truck?.vin ?? '');
  const [ownership, setOwnership] = useState<'owned' | 'external'>(truck?.ownership ?? 'owned');
  const [ownerName, setOwnerName] = useState(truck?.owner_name ?? '');
  const [seller, setSeller] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const clean = vin.toUpperCase().replace(/\s/g, '');
  const save = useMutation({
    mutationFn: () =>
      saveTruck(truck?.id ?? null, category.trim(), clean, ownership, ownerName.trim(), truck ? null : seller),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!category.trim()) return setProblem({ field: 'category', text: t('err.category') });
    if (!VIN.test(clean)) return setProblem({ field: 'vin', text: t('err.vin') });
    if (ownership === 'external' && !ownerName.trim()) return setProblem({ field: 'owner', text: t('err.ownername') });
    if (!truck && !seller) return setProblem({ field: 'seller', text: t('err.seller') });
    setProblem(undefined);
    save.mutate();
  };
  const err = (f: string) => (problem?.field === f ? problem.text : undefined);
  return (
    <BottomSheet title={t(truck ? 'tk.edit' : 'act.newtruck')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        {truck && (
          <Field label={t('tk.ref')}>{({ id }) => <Input id={id} dir="ltr" readOnly value={truck.code} />}</Field>
        )}
        <Field label={t('tk.category')} error={err('category')}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <Field label={t('tk.vin')} hint={t('tk.vinhint', { n: clean.length })} error={err('vin')}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="ltr"
              className="yv-code"
              maxLength={20}
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <Tabs
          label={t('tk.ownership')}
          value={ownership}
          onChange={(v) => setOwnership(v as 'owned' | 'external')}
          items={[
            { id: 'owned', label: t('tk.owned') },
            { id: 'external', label: t('tk.external') },
          ]}
        />
        {ownership === 'external' && (
          <>
            <Field label={t('tk.ownername')} error={err('owner')}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  dir="auto"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                />
              )}
            </Field>
            <Notice tone="info">{t('tk.exthint')}</Notice>
          </>
        )}
        {!truck && (
          <Field label={t('tk.seller')} error={err('seller')}>
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                value={seller}
                onChange={(e) => setSeller(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              >
                <option value="">{t('choose')}</option>
                {choices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('tk.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-RND-03: change a truck's Seller with a reason, from the next round (TRK-03, ADR-101). */
export function SellerSheet({ trucks, onClose }: { trucks: Truck[]; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = useRoundWords();
  const done = useRoundsDone(onClose);
  const [truckId, setTruckId] = useState(trucks[0]?.id ?? '');
  const [seller, setSeller] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const truck = trucks.find((x) => x.id === truckId);
  const { choices } = useSellerChoices(truck?.seller_id);
  const save = useMutation({
    mutationFn: () => changeTruckSeller(truckId, seller, reason.trim()),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!seller) return setProblem({ field: 'seller', text: t('err.seller') });
    if (!reason.trim()) return setProblem({ field: 'reason', text: t('err.reason') });
    setProblem(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={t('cs.save')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t('truck')}>
          {({ id }) => (
            <Select
              id={id}
              value={truckId}
              onChange={(e) => {
                setTruckId(e.target.value);
                setSeller('');
              }}
            >
              {trucks.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.category}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('cs.current')}>
          {({ id }) => <Input id={id} dir="auto" readOnly value={truck?.seller_name ?? ''} />}
        </Field>
        <Field label={t('cs.new')} error={problem?.field === 'seller' ? problem.text : undefined}>
          {({ id, describedBy }) => (
            <Select id={id} value={seller} onChange={(e) => setSeller(e.target.value)} aria-describedby={describedBy}>
              <option value="">{t('choose')}</option>
              {choices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('reason')} error={problem?.field === 'reason' ? problem.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        {problem?.field === 'form' ? (
          <Notice tone="danger">{problem.text}</Notice>
        ) : (
          <p className="muted">{t('cs.hint')}</p>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('cs.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-RND-05: the truck's Seller opens a round, with zero cash (RND-01, ADR-102). */
export function OpenRoundSheet({ truck, onClose }: { truck: Truck; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { refusal } = useRoundWords();
  const done = useRoundsDone(onClose);
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () => openRound(truck.id),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  return (
    <BottomSheet title={t('s55.title')} open onClose={onClose} busy={save.isPending}>
      <div className="yv-skeleton-stack">
        <List label={t('s55.title')}>
          <ListRow lead={{ icon: 'truck' }} title={t('truck')} value={<Code>{truck.code}</Code>} />
          <ListRow
            lead={{ icon: 'users' }}
            title={t('tk.seller')}
            value={truck.seller_name ? <Name>{truck.seller_name}</Name> : '—'}
          />
          <ListRow
            lead={{ icon: 'clock' }}
            title={t('rd.started')}
            value={t('or.now', { d: formatDateTime(new Date(), i18n.language as Language) })}
          />
          <ListRow lead={{ icon: 'cash' }} title={t('rd.cash')} sub={t('or.zero')} />
        </List>
        {problem && <Notice tone="danger">{problem.text}</Notice>}
        <SheetActions>
          <Button variant="main" busy={save.isPending} onClick={() => save.mutate()}>
            {t('or.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </div>
    </BottomSheet>
  );
}

/** SCR-RND-08: end selling (the round's Seller or the owner, RND-02). */
export function EndSellingSheet({ round, onClose }: { round: Round; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = useRoundWords();
  const done = useRoundsDone(onClose);
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () => endSelling(round.id),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  return (
    <BottomSheet title={t('es.title')} open onClose={onClose} busy={save.isPending}>
      <div className="yv-skeleton-stack">
        <p>{t('es.text')}</p>
        {problem && <Notice tone="danger">{problem.text}</Notice>}
        <SheetActions>
          <Button variant="main" busy={save.isPending} onClick={() => save.mutate()}>
            {t('es.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </div>
    </BottomSheet>
  );
}

/** SCR-RND-08: the owner reopens a selling-ended round, with a reason (ADR-103). */
export function ReopenSheet({ round, onClose }: { round: Round; onClose: () => void }) {
  const { t } = useTranslation();
  const { refusal } = useRoundWords();
  const done = useRoundsDone(onClose);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<Problem>();
  const save = useMutation({
    mutationFn: () => reopenRound(round.id, reason.trim()),
    onSuccess: done,
    onError: (e) => setProblem(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setProblem({ field: 'reason', text: t('err.reason') });
    save.mutate();
  };
  return (
    <BottomSheet title={`${t('ro.title')} · ${round.number}`} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {problem?.field === 'form' && <Notice tone="danger">{problem.text}</Notice>}
        <Field label={t('reason')} error={problem?.field === 'reason' ? problem.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('ro.save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

const CONDITIONS = ['sellable', 'damaged'] as const;

/** SCR-RND-06: garage ↔ truck, line by line; on return each line is sellable or damaged (INV-02, ADR-070). */
export function MoveSheet({
  trucks,
  initialTruck,
  garage,
  truckStock,
  onTruck,
  initialDirection = 'load',
  onClose,
}: {
  trucks: Truck[];
  initialTruck: string;
  garage: InventoryRow[];
  truckStock: (truck: string) => InventoryRow[];
  onTruck?: (truck: string) => void;
  initialDirection?: 'load' | 'unload';
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [direction, setDirection] = useState<'load' | 'unload'>(initialDirection);
  const [truck, setTruck] = useState(initialTruck);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string>();
  const source =
    direction === 'load'
      ? garage.filter((r) => r.active || r.calc_pieces > 0)
      : truckStock(truck).filter((r) => r.calc_pieces > 0);
  const save = useMutation({
    mutationFn: () =>
      moveStock(
        truck,
        direction,
        lines.map((x) => ({
          product: x.row.product_id,
          boxes: x.boxes,
          loose: x.loose,
          condition: x.reason === 'damaged' ? 'damaged' : 'sellable',
        })),
      ),
    onSuccess: done,
    onError: (e) => setError(words.refusal(e)),
  });
  const totalBoxes = lines.reduce((s, x) => s + x.boxes, 0);
  const totalLoose = lines.reduce((s, x) => s + x.loose, 0);
  return (
    <LineSheet
      title={t('s56.title')}
      header={
        <>
          <Tabs
            label={t('s56.title')}
            value={direction}
            onChange={(v) => {
              setDirection(v as 'load' | 'unload');
              setLines([]);
            }}
            items={[
              { id: 'load', label: t('mv.dir.load') },
              { id: 'unload', label: t('mv.dir.unload') },
            ]}
          />
          {trucks.length > 1 && (
            <Field label={t('truck')}>
              {({ id }) => (
                <Select
                  id={id}
                  value={truck}
                  onChange={(e) => {
                    setTruck(e.target.value);
                    onTruck?.(e.target.value);
                    setLines([]);
                  }}
                >
                  {trucks.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.code} · {x.seller_name ?? ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </>
      }
      rows={source}
      lines={lines}
      setLines={setLines}
      busy={save.isPending}
      error={error}
      onClose={onClose}
      submitLabel={t('mv.save')}
      summary={`${countLines(t, lines.length)} · ${totalBoxes ? `${countWord(t, 'box', totalBoxes)} + ` : ''}${countWord(t, 'piece', totalLoose)}`}
      onSubmit={() => save.mutate()}
      validateLine={(row, b, l) =>
        b + l === 0
          ? t('err.qty')
          : b > row.boxes || l > row.loose
            ? t('err.overbalance', {
                b: `${row.box_capacity !== null ? `${t('box.n', { n: row.boxes })} + ` : ''}${t('piece.n', { n: row.loose })}`,
              })
            : null
      }
      extra={({ reason, setReason }) =>
        direction === 'unload' ? (
          <Chips
            label={t('reason')}
            items={CONDITIONS.map((c) => ({ id: c, label: t(c === 'sellable' ? 'mv.sellable' : 'mv.damaged') }))}
            selected={[reason === 'damaged' ? 'damaged' : 'sellable']}
            onToggle={(c) => setReason(c)}
          />
        ) : null
      }
      renderSub={(x) =>
        direction === 'unload' ? t(x.reason === 'damaged' ? 'mv.damaged' : 'mv.sellable') : countLines(t, 1)
      }
    />
  );
}

/** SCR-RND-07: count a truck; each difference needs a reason (INV-07; owner and Storekeeper). */
export function TruckCountSheet({ truck, rows, onClose }: { truck: Truck; rows: InventoryRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const words = useStockWords();
  const done = useDone(onClose);
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      countTruck(
        truck.id,
        lines.map((x) => ({
          product: x.row.product_id,
          boxes: x.boxes,
          loose: x.loose,
          reason: x.reason || undefined,
        })),
      ),
    onSuccess: done,
    onError: (e) => setError(e instanceof Refusal && e.code === 'NO_DIFFERENCE' ? t('err.nodiff') : words.refusal(e)),
  });
  return (
    <LineSheet
      title={`${t('tc.title')} · ${truck.code}`}
      rows={rows.filter((r) => r.active || r.calc_pieces > 0)}
      lines={lines}
      setLines={setLines}
      countMode
      busy={save.isPending}
      error={error}
      onClose={onClose}
      submitLabel={t('tc.save')}
      summary={countLines(t, lines.length)}
      onSubmit={() => save.mutate()}
      validateLine={(row, b, l, reason) =>
        (b !== row.boxes || l !== row.loose) && !reason ? t('err.reasondiff') : null
      }
      extra={({ reason, setReason, row, boxes, loose }) =>
        row && boxes !== null && loose !== null && (boxes !== row.boxes || loose !== row.loose) ? (
          <ReasonField value={reason} onChange={setReason} />
        ) : null
      }
      renderSub={(x) => (
        <>
          {t('line.system')} {words.signed(x.row, x.row.boxes, x.row.loose).replace(/\+/g, '')} → {t('line.counted')}{' '}
          {words.signed(x.row, x.boxes, x.loose).replace(/\+/g, '')}
        </>
      )}
    />
  );
}
